if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.form || !this.variantIdInput || !this.submitButton) return;

        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        // Opting a form out of the cart UI entirely. The shopper who presses
        // this button has already chosen a bundle and a plan, so the cart is a
        // step with nothing left to decide in it.
        this.directCheckout = this.dataset.h7DirectCheckout === 'true';
        this.cart = this.directCheckout
          ? null
          : document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        // A direct-checkout buy box owns exactly one line for its product, so
        // it has to know what is already in the cart before the shopper
        // presses anything. Warmed on idle: the press must never wait on this,
        // and the Liquid snapshot the shared state seeds from is only as fresh
        // as a page render, which comes back from bfcache unchanged.
        if (this.directCheckout) this.warmCartState();
        this.submitButtonText = this.submitButton.querySelector('span') || this.submitButton;

        // Only claim a dialog when one can actually open. A direct-checkout form
        // navigates instead, and announcing a popup that never appears sends a
        // screen reader looking for something that is not there.
        if (!this.directCheckout && document.querySelector('cart-drawer')) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // Where a form goes once the line is added, and when.
      //
      // The URL is derived from routes.cart_url rather than written as
      // "/checkout" because that route already carries the market's locale
      // prefix; seventeen catalogues are published, and a hardcoded path drops
      // a French shopper onto the English checkout.
      //
      // The navigation waits one macrotask. H7Analytics.track pushes to
      // dataLayer synchronously, but the GTM and Meta tags listening on it
      // issue their requests asynchronously, and assigning window.location in
      // the same task can cancel those before they leave the browser. Yielding
      // once lets them start. It is a task boundary, not a timed delay, so
      // there is nothing for a shopper to notice.
      checkoutDestination() {
        const cartUrl = window.routes?.cart_url || '/cart';
        return this.directCheckout ? cartUrl.replace(/\/cart$/, '/checkout') : cartUrl;
      }

      // Put the button back in a pressable state. Shared by the bfcache return
      // and the watchdog below, so the two cannot drift apart.
      rearmSubmitButton() {
        window.clearTimeout(this.leaveWatchdog);
        this.leavingForCart = false;
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.classList.remove('loading');
        this.querySelector('.loading__spinner')?.classList.add('hidden');
      }

      // Confirm the cart against Shopify once, away from the critical path, so
      // the surplus check at submit time reads a cart that is actually current.
      // Failures are silent on purpose: this is an optimisation of a check that
      // already degrades safely, never a reason to hold up a buy button.
      warmCartState() {
        const warm = () => {
          try {
            if (window.H7CartState?.needsReconcile?.()) window.H7CartState.get().catch(() => {});
          } catch (error) {
            // No cart state on the page is not an error; the check below copes.
          }
        };
        if (window.requestIdleCallback) window.requestIdleCallback(warm, { timeout: 2000 });
        else window.setTimeout(warm, 0);
      }

      /**
       * The lines this product has in the cart beyond the one just added.
       *
       * A direct-checkout buy box expresses quantity through the option the
       * shopper picked, so two lines of the same product is never something
       * they asked for: it is a second press of the same button. Pressing
       * again means "this is the one I want", so the earlier line goes.
       *
       * Two shapes of surplus, and both can happen at once:
       *   - the same variant and plan, which Shopify folds into the existing
       *     line and hands back with a raised quantity, and
       *   - a different rung or purchase option, which becomes its own line.
       *
       * The first is read straight off the add response and needs no cart
       * state at all, so it is caught even when the state is missing.
       */
      collectSurplusLines(line, quantity) {
        const changes = [];
        if (!line?.key) return changes;

        if (Number(line.quantity) > quantity) changes.push({ id: line.key, quantity });

        const items = window.H7CartState?.peek?.()?.items || [];
        items.forEach((item) => {
          if (!item?.key || item.key === line.key) return;
          if (String(item.product_id) !== String(line.product_id)) return;
          if (Number(item.quantity) > 0) changes.push({ id: item.key, quantity: 0 });
        });

        return changes;
      }

      // Sequential because cart writes serialise on the session anyway, and a
      // parallel pair would only race each other to the same lock.
      async dropSurplusLines(changes) {
        for (const change of changes) {
          try {
            const response = await fetch(`${routes.cart_change_url}`, {
              ...fetchConfig(),
              body: JSON.stringify(change),
            });
            if (!response.ok) return false;
            const cart = await response.json();
            window.H7CartState?.set(cart);
            const updatedLine = cart.items?.find((item) => item.key === change.id);
            if (!Array.isArray(cart.items) || Number(updatedLine?.quantity || 0) !== change.quantity) return false;
          } catch (error) {
            // The line the shopper asked for is already in the cart. A failed
            // tidy-up leaves them with a duplicate, which the cart page can
            // fix; throwing here would strand them on the product page instead.
            console.error('[Hour Seven] could not drop a superseded cart line', error);
            return false;
          }
        }
        return true;
      }

      leaveForCart(destination = this.checkoutDestination()) {
        // Hold the button disabled until navigation commits, so a fast second
        // click cannot issue a second /cart/add.js. bfcache restores the page
        // exactly as it left, so re-arm on the way back in.
        this.leavingForCart = true;
        window.addEventListener('pageshow', () => this.rearmSubmitButton(), { once: true });
        // The line is already in the cart at this point, so the only thing at
        // stake is whether the shopper can act again. If the assignment never
        // commits, a blocked navigation or a checkout that will not load, the
        // button stays disabled with no event to release it and the shopper is
        // left pressing a dead control on a page that has silently succeeded.
        // The watchdog is well past any real navigation and is cleared by the
        // pageshow path on a normal return.
        this.leaveWatchdog = window.setTimeout(() => this.rearmSubmitButton(), 8000);
        setTimeout(() => {
          window.location = destination;
        }, 0);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.form || !this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.error = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          // Server-rendered cart sections cost real time on /cart/add. Only
          // ask for them when the cart element actually paints from that HTML;
          // the sidebar renders from cart JSON and overwrites them anyway.
          if (this.cart.rendersSectionsOnAdd !== false) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }
          this.cart.setActiveElement(document.activeElement);
          // Open on the click, not on the response, so the shopper never waits
          // on the network to see that something happened.
          this.cart.openPending?.();
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) =>
            // A non-2xx is not on its own an error here: Shopify rejects an add
            // with 422 and a JSON body, and the status branch below is what
            // reads it. What is an error is a body that is not JSON at all,
            // which is what a 5xx error page or a 430 from the bot shield
            // returns. That used to surface as a bare SyntaxError from .json()
            // with the status that would have explained it already thrown away.
            //
            // Deliberately still only .json(). Reading .text() first and
            // parsing it by hand gives a better message against a real
            // Response and breaks against every stubbed one, which is what the
            // fixtures in tests/product-state-fixtures.spec.js hand this.
            response.json().then((payload) => {
              if (response.ok === false && !payload.status) {
                return { ...payload, status: response.status || 500 };
              }
              return payload;
            }).catch(() => {
              throw new Error(`Cart add answered ${response.status ?? 'no status'} with a body that is not JSON`);
            })
          )
          .then((response) => {
            if (response.status) {
              const message = response.description || response.message || window.cartStrings?.error;
              window.H7Analytics?.track('validation_error', {
                source: 'add_to_cart',
                variant_id: variantId,
                message: response.description || response.message,
              });
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(message);
              this.dispatchCartErrorEvent(message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(message));
              this.cart?.cancelPending?.(message);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              this.error = false;
              this.submitButtonText.classList.remove('hidden');
              soldOutMessage?.classList.add('hidden');
              return;
            }

            window.H7Analytics?.track('add_to_cart', {
              variant_id: variantId,
              quantity,
              purchase_option: formData.get('selling_plan') ? 'subscription' : 'one-time',
              selling_plan_id: formData.get('selling_plan') || '',
            });

            if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              const surplus = this.collectSurplusLines(response, quantity);
              if (!surplus.length) {
                this.leaveForCart();
                return;
              }
              // Timeboxed against the tidy-up, never against the sale. What the
              // shopper chose is already in the cart by this point, so a slow
              // or hung /cart/change must not hold them on the product page.
              return Promise.race([
                this.dropSurplusLines(surplus),
                new Promise((resolve) => window.setTimeout(() => resolve(false), 2500)),
              ]).then((cleaned) => this.leaveForCart(cleaned ? this.checkoutDestination() : (window.routes?.cart_url || '/cart')));
            }

            // Fold the new line into the shared state once, here, and hand the
            // whole cart to everyone downstream. Subscribers used to receive a
            // bare line item, so each one fetched /cart.js for itself.
            const mergedCart = window.H7CartState?.mergeLine(response) || null;
            this.resolveCartLinesUpdate(linesUpdateDeferred, mergedCart);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            // pubsub calls subscribers synchronously, so one faulty subscriber
            // would otherwise throw into this chain and turn a successful add
            // into the generic could-not-add error path.
            if (!this.error) {
              try {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  source: 'product-form',
                  productVariantId: variantId,
                  cartData: mergedCart || response,
                })
                  .then(() => {
                    CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
                  })
                  .catch(() => {});
              } catch (error) {
                // The add succeeded; a subscriber failure is its own problem.
              }
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            const message = window.cartStrings?.error || 'We could not add this item to your cart. Please try again.';
            // The shopper gets the translated message; the console gets what
            // actually happened. Without this a dropped connection, a 430 and
            // a malformed response are one indistinguishable generic failure
            // in the field, which is the whole reason this path was opaque.
            console.error('[Hour Seven] add to cart failed', e);
            window.H7Analytics?.track('validation_error', {
              source: 'add_to_cart',
              variant_id: variantId,
              message,
            });
            this.handleErrorMessage(message);
            this.dispatchCartErrorEvent(message, 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
            this.cart?.cancelPending?.(message);
            this.error = false;
          })
          .finally(() => {
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && !this.leavingForCart) {
              this.submitButton.classList.remove('loading');
              this.submitButton.removeAttribute('aria-disabled');
              this.querySelector('.loading__spinner')?.classList.add('hidden');
            }

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage && this.errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred, knownCart = null) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        // Prefer the cart we already hold. This event is analytics only, and
        // its own /cart.json fetch used to compete with the sidebar's.
        const pendingCartDataPromise = knownCart?.currency
          ? Promise.resolve(knownCart)
          : typeof CartItems !== 'undefined'
            ? CartItems.fetchCartData()
            : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form?.querySelector('[name=id]');
      }
    }
  );
}
